import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const EVENT_NAME = "srcCopyOverridesChange";
const TABLE = "site_copy_overrides";

type Overrides = Record<string, string>;

const cache: Overrides = {};
let hydrated = false;
let hydrating: Promise<void> | null = null;

function notify() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  }
}

async function hydrate(): Promise<void> {
  if (hydrated) return;
  if (hydrating) return hydrating;
  hydrating = (async () => {
    try {
      const { data, error } = await supabase.from(TABLE).select("key, value");
      if (!error && Array.isArray(data)) {
        for (const row of data as { key: string; value: string }[]) {
          cache[row.key] = row.value;
        }
      }
    } catch {
      /* fall back to defaults */
    } finally {
      hydrated = true;
      notify();
    }
  })();
  return hydrating;
}

function subscribeRealtime() {
  if (typeof window === "undefined") return;
  try {
    supabase
      .channel("site_copy_overrides_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: TABLE },
        (payload: {
          eventType: string;
          new: { key?: string; value?: string } | null;
          old: { key?: string } | null;
        }) => {
          if (payload.eventType === "DELETE") {
            const k = payload.old?.key;
            if (k) delete cache[k];
          } else {
            const row = payload.new;
            if (row?.key && typeof row.value === "string") cache[row.key] = row.value;
          }
          notify();
        }
      )
      .subscribe();
  } catch {
    /* ignore */
  }
}

if (typeof window !== "undefined") {
  hydrate();
  subscribeRealtime();
}

export function getOverride(key: string): string | null {
  return Object.prototype.hasOwnProperty.call(cache, key) ? cache[key] : null;
}

/** Raw override rows (NOT defaults) whose key starts with the given prefix. */
export function getOverridesByPrefix(prefix: string): Overrides {
  const out: Overrides = {};
  for (const k of Object.keys(cache)) {
    if (k.startsWith(prefix)) out[k] = cache[k];
  }
  return out;
}

/** Reads ?promo_preview=banner|popup from the current URL. */
export function promoPreview(): "banner" | "popup" | null {
  if (typeof window === "undefined") return null;
  try {
    const v = new URLSearchParams(window.location.search).get("promo_preview");
    return v === "banner" || v === "popup" ? v : null;
  } catch {
    return null;
  }
}

/** Sanitized CSS color override (empty string when unset or too long). */
export function useColorOverride(key: string): string {
  const raw = useCopyOverride(key, "");
  const v = raw.trim();
  return v && v.length <= 40 ? v : "";
}


export async function setOverride(key: string, value: string): Promise<void> {
  const previous = cache[key];
  cache[key] = value;
  notify();
  try {
    const { error } = await supabase
      .from(TABLE)
      .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" });
    if (error) throw error;
  } catch (err) {
    if (previous === undefined) delete cache[key];
    else cache[key] = previous;
    notify();
    console.error("setOverride failed:", err);
    throw err;
  }
}

export async function clearOverride(key: string): Promise<void> {
  const previous = cache[key];
  delete cache[key];
  notify();
  try {
    const { error } = await supabase.from(TABLE).delete().eq("key", key);
    if (error) throw error;
  } catch (err) {
    if (previous !== undefined) cache[key] = previous;
    notify();
    console.error("clearOverride failed:", err);
    throw err;
  }
}

export function useCopyOverride(key: string, fallback: string): string {
  const [value, setValue] = useState<string>(() => getOverride(key) ?? fallback);
  useEffect(() => {
    const sync = () => setValue(getOverride(key) ?? fallback);
    if (!hydrated) hydrate().then(sync);
    else sync();
    window.addEventListener(EVENT_NAME, sync);
    return () => window.removeEventListener(EVENT_NAME, sync);
  }, [key, fallback]);
  return value;
}

// 🎯 Son's Blue River Camp defaults
export const COPY_DEFAULTS = {
  "banner.desktop":
    "🌊 Son's Blue River Camp — Riverfront Cabins, Glamping & Tubing on the San Marcos River — BOOK NOW! 🏕️",
  "banner.mobile":
    "🌊 Blue River Camp — BOOK NOW! 🏕️",
  "banner.href": "/book-now?intent=overnight&step=2",
  "popup.headline": "Looking for availability this weekend?",
  "popup.price": "Glamping cabins from $99!",
  "popup.subline": "Cabins include:",
  "popup.cta": "Book Now",
  "popup.accommodations": "glamping_cabin",
  "popup.href": "/book-now?intent=overnight&step=2",
  "popup.image": "gallery-river-scene",
  "popup.delayMs": "10000",
  "popup.images": "",
  "popup.cards": "",
  "banner.bg": "",
  "banner.fg": "",
  "popup.accent": "",
  "popup.buttonFg": "",

  "banner.enabled": "true",
  "banner.startAt": "",
  "banner.endAt": "",
  "popup.enabled": "true",
  "popup.startAt": "",
  "popup.endAt": "",
  "control.banner.customPresets": "",
  "control.banner.drafts": "",
  "control.banner.labels": "",
  "control.banner.activeId": "",
  "control.popup.customPresets": "",
  "control.popup.drafts": "",
  "control.popup.labels": "",
  "control.popup.activeId": "",
} as const;

export type CopyKey = keyof typeof COPY_DEFAULTS;

function parseTs(raw: string | null): number | null {
  if (!raw) return null;
  const t = Date.parse(raw.trim());
  return Number.isFinite(t) ? t : null;
}

export function isPromoActive(prefix: "banner" | "popup", now: number = Date.now()): boolean {
  if ((getOverride(`${prefix}.enabled`) ?? "true") === "false") return false;
  const start = parseTs(getOverride(`${prefix}.startAt`));
  if (start !== null && start > now) return false;
  const end = parseTs(getOverride(`${prefix}.endAt`));
  if (end !== null && end < now) return false;
  return true;
}

export function usePromoActive(prefix: "banner" | "popup"): boolean {
  const [active, setActive] = useState<boolean>(() => isPromoActive(prefix));
  useEffect(() => {
    const sync = () => setActive(isPromoActive(prefix));
    if (!hydrated) hydrate().then(sync);
    else sync();
    window.addEventListener(EVENT_NAME, sync);
    const id = window.setInterval(sync, 60000);
    return () => {
      window.removeEventListener(EVENT_NAME, sync);
      window.clearInterval(id);
    };
  }, [prefix]);
  return active;
}

export function useAllOverrides(): [Overrides, () => void] {
  const [all, setAll] = useState<Overrides>(() => ({ ...cache }));
  const refresh = useCallback(() => setAll({ ...cache }), []);
  useEffect(() => {
    const sync = () => setAll({ ...cache });
    sync();
    window.addEventListener(EVENT_NAME, sync);
    return () => window.removeEventListener(EVENT_NAME, sync);
  }, []);
  return [all, refresh];
}